import React, { useState, useEffect, useRef } from 'react'
import { useSettingsStore, Settings } from '@/store/settingsStore'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { authService } from '@/services/authService'
import { apiService } from '@/services/api'
import type { BillCategory } from '@/constants/billTypes'
import { Plus, Pencil, Trash2 } from 'lucide-react'

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', JPY: '¥', LKR: 'Rs.', INR: '₹'
}

export const SettingsPage: React.FC = () => {
  const { settings, setSettings, resetSettings } = useSettingsStore()
  const { showSuccess, showError } = useNotificationActions()

  const [localSettings, setLocalSettings] = useState<Settings>(settings)
  const [activeTab, setActiveTab] = useState('general')
  const [isSaving, setIsSaving] = useState(false)

  const [changePasswordData, setChangePasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  const [isBackupLoading, setIsBackupLoading] = useState(false)
  const [isRestoreLoading, setIsRestoreLoading] = useState(false)
  const [restoreConfirm, setRestoreConfirm] = useState('')
  const [restoreFile, setRestoreFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [auditLogs, setAuditLogs] = useState<Array<{ id: string; action: string; userName?: string; userId?: string; resourceType?: string; resourceId?: string; details?: unknown; createdAt?: string }>>([])
  const [auditLoading, setAuditLoading] = useState(false)

  const [billCategories, setBillCategories] = useState<BillCategory[]>([])
  const [billCategoriesLoading, setBillCategoriesLoading] = useState(false)
  const [billCategoryModal, setBillCategoryModal] = useState<{ open: true; category?: BillCategory } | { open: false }>({ open: false })
  const [billCategoryForm, setBillCategoryForm] = useState({ label: '', icon: '📄', description: '' })
  const [billCategorySaving, setBillCategorySaving] = useState(false)

  useEffect(() => {
    setLocalSettings(settings)
  }, [settings])

  useEffect(() => {
    if (activeTab !== 'audit') return
    setAuditLoading(true)
    apiService.get<{ success: boolean; data: typeof auditLogs }>('/admin/audit')
      .then((res: any) => {
        if (res?.success && Array.isArray(res.data)) setAuditLogs(res.data)
        else setAuditLogs([])
      })
      .catch(() => setAuditLogs([]))
      .finally(() => setAuditLoading(false))
  }, [activeTab])

  useEffect(() => {
    if (activeTab !== 'bill-categories') return
    setBillCategoriesLoading(true)
    apiService.get<{ success: boolean; data: BillCategory[] }>('/bill-categories')
      .then((res: any) => {
        if (res?.success && Array.isArray(res.data)) setBillCategories(res.data)
        else setBillCategories([])
      })
      .catch(() => setBillCategories([]))
      .finally(() => setBillCategoriesLoading(false))
  }, [activeTab])

  const tabs = [
    { id: 'general', name: 'General', icon: '⚙️' },
    { id: 'business', name: 'Business Info', icon: '🏪' },
    { id: 'notifications', name: 'Notifications', icon: '🔔' },
    { id: 'security', name: 'Security', icon: '🔒' },
    { id: 'bill-categories', name: 'Bill Categories', icon: '💳' },
    { id: 'audit', name: 'Audit Log', icon: '📋' },
    { id: 'backup', name: 'Backup', icon: '💾' }
  ]

  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'LKR', 'INR']
  const timezones = [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Europe/London', 'Europe/Paris', 'Asia/Kolkata', 'Asia/Colombo', 'Asia/Tokyo', 'Australia/Sydney'
  ]
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'zh', name: 'Chinese' },
    { code: 'si', name: 'Sinhala' },
    { code: 'ta', name: 'Tamil' }
  ]

  const handleSave = async () => {
    setIsSaving(true)
    try {
      setSettings(localSettings)
      showSuccess('Settings Saved', 'Your preferences have been updated.')
    } catch (err) {
      showError('Save Failed', 'Could not save settings.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    if (confirm('Reset all settings to default?')) {
      resetSettings()
      setLocalSettings(useSettingsStore.getState().settings)
      showSuccess('Settings Reset', 'All settings restored to default.')
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    const { currentPassword, newPassword, confirmPassword } = changePasswordData
    if (!currentPassword || !newPassword || !confirmPassword) {
      showError('Missing Fields', 'Please fill in all password fields.')
      return
    }
    if (newPassword !== confirmPassword) {
      showError('Mismatch', 'New password and confirmation do not match.')
      return
    }
    if (newPassword.length < 6) {
      showError('Weak Password', 'New password must be at least 6 characters.')
      return
    }
    setIsChangingPassword(true)
    try {
      await authService.changePassword({ currentPassword, newPassword, confirmPassword })
      setChangePasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      showSuccess('Password Changed', 'Your password has been updated successfully.')
    } catch (err: any) {
      showError('Change Failed', err?.message || 'Could not change password.')
    } finally {
      setIsChangingPassword(false)
    }
  }

  const updateLocal = (updates: Partial<Settings>) => {
    setLocalSettings((prev) => ({ ...prev, ...updates }))
  }

  const openBillCategoryModal = (category?: BillCategory) => {
    if (category) {
      setBillCategoryForm({ label: category.label, icon: category.icon || '📄', description: category.description || '' })
      setBillCategoryModal({ open: true, category })
    } else {
      setBillCategoryForm({ label: '', icon: '📄', description: '' })
      setBillCategoryModal({ open: true })
    }
  }

  const closeBillCategoryModal = () => {
    setBillCategoryModal({ open: false })
    setBillCategoryForm({ label: '', icon: '📄', description: '' })
  }

  const saveBillCategory = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!billCategoryForm.label.trim()) {
      showError('Required', 'Label is required.')
      return
    }
    setBillCategorySaving(true)
    try {
      if (billCategoryModal.open && billCategoryModal.category) {
        const res = await apiService.patch<{ success: boolean; data: BillCategory }>(
          `/bill-categories/${billCategoryModal.category.id}`,
          { label: billCategoryForm.label.trim(), icon: billCategoryForm.icon.trim() || '📄', description: billCategoryForm.description.trim() }
        )
        if (res?.success && res.data) {
          setBillCategories((prev) => prev.map((c) => (c.id === res.data.id ? res.data : c)))
          showSuccess('Updated', `"${billCategoryForm.label}" has been updated.`)
          closeBillCategoryModal()
        }
      } else {
        const res = await apiService.post<{ success: boolean; data: BillCategory }>('/bill-categories', {
          label: billCategoryForm.label.trim(),
          icon: billCategoryForm.icon.trim() || '📄',
          description: billCategoryForm.description.trim(),
        })
        if (res?.success && res.data) {
          setBillCategories((prev) => [...prev, res.data].sort((a, b) => (a.order ?? 999) - (b.order ?? 999)))
          showSuccess('Added', `"${billCategoryForm.label}" has been added.`)
          closeBillCategoryModal()
        }
      }
    } catch (err: any) {
      showError('Error', err?.response?.data?.message || 'Could not save bill category.')
    } finally {
      setBillCategorySaving(false)
    }
  }

  const deleteBillCategory = async (cat: BillCategory) => {
    if (!window.confirm(`Delete "${cat.label}"? This cannot be undone.`)) return
    try {
      await apiService.delete(`/bill-categories/${cat.id}`)
      setBillCategories((prev) => prev.filter((c) => c.id !== cat.id))
      showSuccess('Deleted', `"${cat.label}" has been removed.`)
    } catch (err: any) {
      showError('Error', err?.response?.data?.message || 'Could not delete.')
    }
  }

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Store Name</label>
        <input
          type="text"
          value={localSettings.storeName}
          onChange={(e) => updateLocal({ storeName: e.target.value })}
          className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Currency</label>
        <select
          value={localSettings.currency}
          onChange={(e) => {
            const cur = e.target.value
            updateLocal({
              currency: cur,
              currencySymbol: CURRENCY_SYMBOLS[cur] || cur,
            })
          }}
          className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {currencies.map((c) => (
            <option key={c} value={c}>{c} {CURRENCY_SYMBOLS[c] ? `(${CURRENCY_SYMBOLS[c]})` : ''}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Tax Rate (%)</label>
        <input
          type="number"
          step="0.1"
          min="0"
          value={localSettings.taxRate}
          onChange={(e) => updateLocal({ taxRate: parseFloat(e.target.value) || 0 })}
          className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Timezone</label>
        <select
          value={localSettings.timezone}
          onChange={(e) => updateLocal({ timezone: e.target.value })}
          className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {timezones.map((tz) => (
            <option key={tz} value={tz}>{tz}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Language</label>
        <select
          value={localSettings.language}
          onChange={(e) => updateLocal({ language: e.target.value })}
          className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>{lang.name}</option>
          ))}
        </select>
      </div>
    </div>
  )

  const renderBusinessSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Store Address</label>
        <textarea
          value={localSettings.storeAddress}
          onChange={(e) => updateLocal({ storeAddress: e.target.value })}
          rows={3}
          className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Phone Number</label>
          <input
            type="tel"
            value={localSettings.storePhone}
            onChange={(e) => updateLocal({ storePhone: e.target.value })}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Email Address</label>
          <input
            type="email"
            value={localSettings.storeEmail}
            onChange={(e) => updateLocal({ storeEmail: e.target.value })}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Receipt Header</label>
        <input
          type="text"
          value={localSettings.receiptHeader}
          onChange={(e) => updateLocal({ receiptHeader: e.target.value })}
          className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Receipt Footer</label>
        <textarea
          value={localSettings.receiptFooter}
          onChange={(e) => updateLocal({ receiptFooter: e.target.value })}
          rows={3}
          className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
    </div>
  )

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-foreground font-medium">Email Notifications</h3>
          <p className="text-muted-foreground text-sm">Receive low stock and sales alerts via email</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={localSettings.emailNotifications}
            onChange={(e) => updateLocal({ emailNotifications: e.target.checked })}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-foreground font-medium">SMS Notifications</h3>
          <p className="text-muted-foreground text-sm">Receive critical alerts via SMS</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={localSettings.smsNotifications}
            onChange={(e) => updateLocal({ smsNotifications: e.target.checked })}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-foreground font-medium">Low Stock Alerts</h3>
          <p className="text-muted-foreground text-sm">Alert when product stock falls below threshold</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={localSettings.lowStockAlertEnabled}
            onChange={(e) => updateLocal({ lowStockAlertEnabled: e.target.checked })}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Low Stock Threshold</label>
        <input
          type="number"
          min="1"
          value={localSettings.lowStockThreshold}
          onChange={(e) => updateLocal({ lowStockThreshold: parseInt(e.target.value, 10) || 1 })}
          className="w-full max-w-[120px] px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
    </div>
  )

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-muted-foreground text-sm font-medium mb-2">Session timeout (minutes)</label>
        <input
          type="number"
          min={0}
          max={480}
          value={localSettings.sessionTimeoutMinutes ?? 30}
          onChange={(e) => updateLocal({ sessionTimeoutMinutes: Math.max(0, parseInt(e.target.value, 10) || 0) })}
          className="w-full max-w-[120px] px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <p className="text-xs text-muted-foreground mt-1">Auto log-out after this many minutes of inactivity. Set to 0 to disable.</p>
      </div>

      <form onSubmit={handleChangePassword} className="space-y-6">
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Current Password</label>
          <input
            type="password"
            value={changePasswordData.currentPassword}
            onChange={(e) => setChangePasswordData((p) => ({ ...p, currentPassword: e.target.value }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter current password"
          />
        </div>
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">New Password</label>
          <input
            type="password"
            value={changePasswordData.newPassword}
            onChange={(e) => setChangePasswordData((p) => ({ ...p, newPassword: e.target.value }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="At least 6 characters"
          />
        </div>
        <div>
          <label className="block text-muted-foreground text-sm font-medium mb-2">Confirm New Password</label>
          <input
            type="password"
            value={changePasswordData.confirmPassword}
            onChange={(e) => setChangePasswordData((p) => ({ ...p, confirmPassword: e.target.value }))}
            className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Confirm new password"
          />
        </div>
        <button
          type="submit"
          disabled={isChangingPassword}
          className="bg-gradient-to-r from-primary to-secondary text-white font-semibold py-2 px-6 rounded-lg hover:from-primary/90 hover:to-secondary/90 transition-all duration-300 disabled:opacity-50"
        >
          {isChangingPassword ? 'Changing...' : 'Change Password'}
        </button>
      </form>
    </div>
  )

  const renderAuditSettings = () => (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">Recent security and transaction events. Admin only.</p>
      {auditLoading ? (
        <div className="py-8 text-center text-muted-foreground">Loading audit log...</div>
      ) : auditLogs.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">No audit entries yet.</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Time</th>
                <th className="text-left px-4 py-3 font-medium">Action</th>
                <th className="text-left px-4 py-3 font-medium">User</th>
                <th className="text-left px-4 py-3 font-medium">Resource</th>
                <th className="text-left px-4 py-3 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((log) => (
                <tr key={log.id} className="border-t border-border hover:bg-muted/20">
                  <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                    {log.createdAt ? new Date(log.createdAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 font-medium">{log.action || '—'}</td>
                  <td className="px-4 py-2">{log.userName || log.userId || '—'}</td>
                  <td className="px-4 py-2">{log.resourceType ? `${log.resourceType}${log.resourceId ? ` #${log.resourceId}` : ''}` : '—'}</td>
                  <td className="px-4 py-2 text-muted-foreground max-w-xs truncate">
                    {log.details != null ? (typeof log.details === 'object' ? JSON.stringify(log.details) : String(log.details)) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  const handleDownloadBackup = async () => {
    setIsBackupLoading(true)
    try {
      const res = await apiService.get<{ success: boolean; data: { backup: unknown; exportedAt: string } }>('/admin/backup')
      if (!res.success || !res.data?.backup) {
        showError('Backup Failed', 'Could not export backup.')
        return
      }
      const blob = new Blob([JSON.stringify({ backup: res.data.backup, exportedAt: res.data.exportedAt }, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `esales-backup-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      showSuccess('Backup Downloaded', 'Your data has been exported.')
    } catch (err: any) {
      showError('Backup Failed', err?.response?.data?.message || 'Could not download backup. Admin only.')
    } finally {
      setIsBackupLoading(false)
    }
  }

  const handleRestore = async () => {
    if (restoreConfirm !== 'RESTORE') {
      showError('Confirmation Required', 'Type RESTORE to confirm.')
      return
    }
    if (!restoreFile) {
      showError('No File', 'Select a backup file first.')
      return
    }
    setIsRestoreLoading(true)
    try {
      const text = await restoreFile.text()
      const parsed = JSON.parse(text)
      const backup = parsed.backup ?? parsed
      await apiService.post('/admin/restore', { backup, confirm: 'RESTORE' })
      showSuccess('Restore Completed', 'Data has been restored. You may need to refresh the app.')
      setRestoreConfirm('')
      setRestoreFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err: any) {
      showError('Restore Failed', err?.response?.data?.message || 'Could not restore. Check file and try again.')
    } finally {
      setIsRestoreLoading(false)
    }
  }

  const renderBillCategoriesSettings = () => (
    <div className="space-y-6">
      <p className="text-muted-foreground text-sm">
        Manage billing categories shown on the Bill Payments page (e.g. Mobile Reload, Electric Bill). Add, edit, or remove categories.
      </p>
      {billCategoriesLoading ? (
        <div className="py-8 text-center text-muted-foreground">Loading categories...</div>
      ) : (
        <>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => openBillCategoryModal()}
              className="flex items-center gap-2 bg-primary text-primary-foreground font-semibold py-2 px-4 rounded-lg hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" />
              Add category
            </button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Icon</th>
                  <th className="text-left px-4 py-3 font-medium">Label</th>
                  <th className="text-left px-4 py-3 font-medium">Description</th>
                  <th className="text-right px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {billCategories.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No bill categories yet. Add one above.</td>
                  </tr>
                ) : (
                  billCategories.map((cat) => (
                    <tr key={cat.id} className="border-t border-border hover:bg-muted/20">
                      <td className="px-4 py-3 text-2xl">{cat.icon || '📄'}</td>
                      <td className="px-4 py-3 font-medium">{cat.label}</td>
                      <td className="px-4 py-3 text-muted-foreground">{cat.description || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => openBillCategoryModal(cat)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 mr-2"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteBillCategory(cat)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {billCategoryModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeBillCategoryModal}>
          <div className="bg-background border border-border rounded-2xl shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">{billCategoryModal.category ? 'Edit category' : 'Add category'}</h3>
            <form onSubmit={saveBillCategory} className="space-y-4">
              <div>
                <label className="block text-muted-foreground text-sm font-medium mb-1">Label *</label>
                <input
                  type="text"
                  value={billCategoryForm.label}
                  onChange={(e) => setBillCategoryForm((p) => ({ ...p, label: e.target.value }))}
                  placeholder="e.g. Mobile Reload"
                  className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-muted-foreground text-sm font-medium mb-1">Icon (emoji)</label>
                <input
                  type="text"
                  value={billCategoryForm.icon}
                  onChange={(e) => setBillCategoryForm((p) => ({ ...p, icon: e.target.value || '📄' }))}
                  placeholder="📱"
                  className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none max-w-[80px] text-2xl text-center"
                />
              </div>
              <div>
                <label className="block text-muted-foreground text-sm font-medium mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={billCategoryForm.description}
                  onChange={(e) => setBillCategoryForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Prepaid top-up & reloads"
                  className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeBillCategoryModal} className="px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 font-medium">
                  Cancel
                </button>
                <button type="submit" disabled={billCategorySaving} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50">
                  {billCategorySaving ? 'Saving...' : billCategoryModal.category ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )

  const renderBackupSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-foreground font-medium">Automatic Backup</h3>
          <p className="text-muted-foreground text-sm">Save backup preference (schedule not implemented)</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={localSettings.autoBackup}
            onChange={(e) => updateLocal({ autoBackup: e.target.checked })}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      <div className="bg-background/50 rounded-lg p-4 space-y-4">
        <h4 className="text-foreground font-medium">Download Backup</h4>
        <p className="text-muted-foreground text-sm">
          Export all POS data (products, categories, transactions, customers, etc.) as a JSON file. Admin only.
        </p>
        <button
          type="button"
          onClick={handleDownloadBackup}
          disabled={isBackupLoading}
          className="bg-primary text-primary-foreground font-semibold py-2 px-4 rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          {isBackupLoading ? 'Exporting...' : 'Download backup'}
        </button>
      </div>

      <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 space-y-4">
        <h4 className="text-foreground font-medium text-destructive">Restore from Backup</h4>
        <p className="text-muted-foreground text-sm">
          Restore replaces all current data with the backup. This cannot be undone. Admin only.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-muted file:text-foreground"
          onChange={(e) => setRestoreFile(e.target.files?.[0] ?? null)}
        />
        <input
          type="text"
          placeholder='Type RESTORE to confirm'
          value={restoreConfirm}
          onChange={(e) => setRestoreConfirm(e.target.value)}
          className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button
          type="button"
          onClick={handleRestore}
          disabled={isRestoreLoading || restoreConfirm !== 'RESTORE' || !restoreFile}
          className="bg-destructive text-destructive-foreground font-semibold py-2 px-4 rounded-lg hover:bg-destructive/90 disabled:opacity-50"
        >
          {isRestoreLoading ? 'Restoring...' : 'Restore from file'}
        </button>
      </div>
    </div>
  )

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'general': return renderGeneralSettings()
      case 'business': return renderBusinessSettings()
      case 'notifications': return renderNotificationSettings()
      case 'security': return renderSecuritySettings()
      case 'bill-categories': return renderBillCategoriesSettings()
      case 'audit': return renderAuditSettings()
      case 'backup': return renderBackupSettings()
      default: return renderGeneralSettings()
    }
  }

  return (
    <div className="w-full py-6">
      <div className="py-6">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-2">Configure your POS system preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="glass-card">
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-primary to-secondary text-white'
                        : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                    }`}
                  >
                    <span className="mr-3">{tab.icon}</span>
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="glass-card">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gradient-esales capitalize">{activeTab} Settings</h2>
                <p className="text-muted-foreground mt-1">
                  {activeTab === 'general' && 'Configure general system settings'}
                  {activeTab === 'business' && 'Manage your business information'}
                  {activeTab === 'notifications' && 'Set up notification preferences'}
                  {activeTab === 'security' && 'Manage security and authentication'}
                  {activeTab === 'bill-categories' && 'Manage billing categories for Bill Payments'}
                  {activeTab === 'backup' && 'Configure backup preferences'}
                  {activeTab === 'audit' && 'View security and transaction audit log'}
                </p>
              </div>

              {renderActiveTab()}

              {activeTab !== 'security' && activeTab !== 'audit' && activeTab !== 'bill-categories' && (
                <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-border">
                  <button
                    onClick={handleReset}
                    className="bg-background/50 text-foreground font-semibold py-2 px-6 rounded-lg hover:bg-background/80 transition-all duration-300"
                  >
                    Reset to Default
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="bg-gradient-to-r from-primary to-secondary text-white font-semibold py-2 px-6 rounded-lg hover:from-primary/90 hover:to-secondary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
